import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../../Hooks/useNavigateAndScroll';

const ImmunizationSchedule = () => {

  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();
  const infoCardsData = {
    expectantParents: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/expectant_parents.webp",
      infoTitle: "Expectant Parents",
      infoText: "Comprehensive prenatal care, guidance, and support for expecting mothers and families preparing for their new arrival.",
      path: "/services/expectant-parents"
    },
    wellChecks: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Well+Check.webp",
      infoTitle: "Well Checks", 
      infoText: "Regular preventive care appointments to monitor your child's growth, development, and overall health at every stage.",
      path: "/services/well-checks"
    },
    sameDaySickVisits: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/sick+same+day.webp",
      infoTitle: "Same Day Sick Visits",
      infoText: "Prompt medical attention for acute illnesses and urgent health concerns when your child needs immediate care.",
      path: "/services/same-day-sick-visits"
    },
    functionalMedicine: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Functional+Medicine.webp",
      infoTitle: "Functional Medicine",
      infoText: "Holistic approach to healthcare focusing on identifying and addressing the root causes of health issues for optimal wellness.",
      path: "/services/functional-medicine"
    },
    commonMedicalConditions: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/common+medical+conditions.webp",
      infoTitle: "Common Medical Conditions",
      infoText: "Information and treatment options for frequently encountered pediatric and family health conditions and concerns.",
      path: "/services/common-medical-conditions"
    }
  };


  return (
    <div>
      <div className="relative w-auto z-1 mt-[-70px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-forms.s3.us-east-2.amazonaws.com/well_checks_short_InPixio.webp"
            alt="Mother and child meeting a doctor"
          />
        </div>
    <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-[100]">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>
    <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
      <h2 className="text-4xl font-bold text-gray-800 mb-2">Well Checks</h2>
      <hr className="border-t-2 border-blue-500 mb-6" />
      <div className="mb-6">
  <p className="text-gray-600 mb-4">
    Well-child checks, also known as well-child visits or pediatric check-ups, are routine medical
    appointments for children designed to monitor their growth and development, provide preventive care,
    and address any health concerns. These visits are important for maintaining a child’s overall health and
    ensuring that they meet developmental milestones.
  </p>
  <p className="text-gray-600 mb-4">
    Well-child checks are vital for ensuring that children grow up healthy and develop appropriately. Parents
    are encouraged to keep up with the recommended schedule and actively participate in their child’s
    health care.
  </p>
  <p className="text-gray-600 mb-4">
    After your visit, you will receive the <span className="font-semibold">"State of Illinois Certificate of Child Health Examination."</span> This
    certificate is required for school admission to kindergarten, sixth grade, and ninth grade. Additionally, it
    may also be used as proof of examination for interscholastic sports.
  </p>

  <h3 className="text-2xl font-bold text-gray-800 mb-2">Key Components of Well-Child Checks</h3>
  <ul className="list-disc list-outside ml-5 text-gray-600 space-y-4">
    <li>
      <span className="font-semibold">Growth Monitoring:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Measuring height, weight, and head circumference (for infants) to assess growth patterns.</li>
        <li>Comparing growth against standardized growth charts.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Developmental Screening:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Assessing physical, cognitive, and emotional development.</li>
        <li>We use standardized screening tools to identify potential developmental delays like ASQ and MCAT.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Immunizations:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Administering vaccines according to the AAP recommended immunization schedule.</li>
        <li>Discussing the importance of vaccinations for disease prevention.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Physical Examination:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Conducting a comprehensive physical exam, including checks of the heart, lungs, abdomen, and skin.</li>
        <li>Evaluating vision and hearing as needed.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Health Education and Counseling:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Providing guidance on nutrition, physical activity, and healthy lifestyle choices.</li>
        <li>Addressing topics like safety, sleep, and social development.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Mental Health Assessment:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Discussing the child’s emotional and social well-being.</li>
        <li>Screening for conditions like depression and substance abuse in adolescents.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Screening Blood Tests:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Screening for anemia and lead toxicity at AAP recommended ages.</li>
      </ul>
    </li>
    <li>
      <span className="font-semibold">Discussion of Family Concerns:</span>
      <ul className="list-disc list-inside ml-5">
        <li>Allowing parents to voice any concerns or questions about their child’s health or behavior.</li>
        <li>Providing resources and referrals if necessary.</li>
      </ul>
    </li>
  </ul>

  <div className="mt-6 mb-2 flex items-center justify-between gap-3 flex-wrap">
    <h3 className="text-2xl font-bold text-gray-800">
      Recommended Schedule for Well-Child Checks
    </h3>
    <a
      href="https://cipa-website-forms.s3.us-east-2.amazonaws.com/AGE_FOR_EXAM_final_(2020_01_18_15_39_25_UTC).pdf"
      target="_blank"
      rel="noopener noreferrer"
    download="Well-Child-Checks-Schedule.pdf"
     className="inline-flex items-center rounded-md bg-custom-regularOrange px-4 py-2 text-white font-semibold shadow hover:bg-custom-darkOrange focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-custom-regularOrange"
   >
      Click here to download form
    </a>
 </div>
</div>

      <hr className="border-t-2 border-blue-500 mb-6" />
      
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-4 py-2 text-left text-gray-800 font-bold">AGE</th>
              <th className="border px-4 py-2 text-left text-gray-800 font-bold">SCREENING QUESTIONNAIRES</th>
              <th className="border px-4 py-2 text-left text-gray-800 font-bold">SCREENING TEST</th>
              <th className="border px-4 py-2 text-left text-gray-800 font-bold">IMMUNIZATIONS</th>
            </tr>
          </thead>
          <tbody>
          <tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">3-7 DAYS</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">NO IMMUNIZATIONS</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">2 WEEKS</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">NO IMMUNIZATIONS</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">1 MONTH</td>
  <td className="border px-4 py-2 text-gray-600">Postpartum Depression Screen, TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">NO IMMUNIZATIONS</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">2 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Postpartum Depression Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. Pentacel (
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/dtap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DTaP</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/ipv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">IPV</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hib.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hib</a>)<br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/pcv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Prevnar</a><br />
    3. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hep-b.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hepatitis B</a><br />
    4. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/rotavirus.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Rotavirus</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">4 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Postpartum Depression Screen, Anemia Screen (Hemoglobin)</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. Pentacel (
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/dtap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DTaP</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/ipv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">IPV</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hib.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hib</a>)<br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/pcv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Prevnar</a><br />
    3. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/rotavirus.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Rotavirus</a><br />
    (*<a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hep-b.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hepatitis B</a>)
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">6 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Postpartum Depression Screen, Lead & TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. Pentacel (
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/dtap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DTaP</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/ipv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">IPV</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hib.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hib</a>)<br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/pcv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Prevnar</a><br />
    3. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/rotavirus.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Rotavirus</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">9 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen, ASQ-3 Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hep-b.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hepatitis B</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">12 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">Lead, Hemoglobin</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/varicella.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Varicella</a><br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/mmr.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">MMR</a><br />
    3. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hep-a.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hepatitis A</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">15 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. Pentacel (
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/dtap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DTaP</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/ipv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">IPV</a> +
      <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hib.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hib</a>)<br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/pcv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Prevnar</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">18 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">M-CHAT, Lead Screen, TB Screen, ASQ-3 Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hep-a.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Hepatitis A</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">24 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">M-CHAT, TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">Lead</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">30 MONTHS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen, ASQ-3 Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>

<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">3 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">Vision</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">4 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/varicella.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Varicella</a><br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/mmr.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">MMR</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">5 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">Vision (Ophthalmologist)</td>
  <td className="border px-4 py-2 text-gray-600">Quadracel (<a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/dtap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DTaP</a> + <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/ipv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">IPV</a>)</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">6 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">Lead Screen, TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">7 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">8 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">9 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB Screen, Dyslipidemia Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">10 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS, Lipid Profile</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">11 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Dyslipidemia</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/tdap.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">TdaP</a><br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/mening.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Meningococcal ACYW</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">12 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/hpv.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">HPV</a> (2 Doses)
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">13 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">14 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">15 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">16 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">
    1. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/mening.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Meningococcal ACYW</a><br />
    2. <a href="https://www.cdc.gov/vaccines/hcp/vis/vis-statements/mening-serogroup.pdf" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">Meningococcal B</a>
  </td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">17 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">18 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS, Lipid Profile</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">19 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">20 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>
<tr className="hover:bg-gray-50">
  <td className="border px-4 py-2 text-gray-600">21 YEARS</td>
  <td className="border px-4 py-2 text-gray-600">TB, Depression, Dyslipidemia & CRAFFT Screen</td>
  <td className="border px-4 py-2 text-gray-600">NO LABS</td>
  <td className="border px-4 py-2 text-gray-600">ONLY IF NEEDS CATCH-UP VACCINE</td>
</tr>

            {/* Notes section in merged cell */}
            <tr>
              <td colSpan="4" className="border px-4 py-4 text-gray-600">
                <div className="space-y-2">
                  <p className="font-semibold">YEARLY INFLUENZA VACCINE (INACTIVATED OR LIVE NASAL) IS RECOMMENDED FOR ALL PATIENTS OF MORE THAN 6 MONTHS OF AGE</p>
                  <p>RSV vaccine is recommended for all infants born in RSV season, from birth to 8 months of age in first year of life</p>
                  <p>We offer COVID-19 Vaccine at our facility</p>
                  <p>TYPHOID VACCINE FOR CHILD (2 years and above) Travelling, moving or studying in high risk area</p>
                  <p>*HEPATITIS B –If did not receive 1st dose at birth</p>
                  <p>HEMOGLOBIN, TB TEST, URINALYSIS and CHLAMYDIA will be done only if high risk factors are present</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

export default ImmunizationSchedule;